import { useCallback, useEffect, useState } from 'react';
import { LocalJsonStorage } from 'utils/storage';

function storageKey(organizationId) {
  return `prep-sheet-checklist/${organizationId}`;
}

function readStored(organizationId) {
  if (!organizationId) {
    return {};
  }
  return LocalJsonStorage.get(storageKey(organizationId)) || {};
}

export default function usePrepSheetChecklist(organizationId) {
  const [checkedByOrder, setCheckedByOrder] = useState(() => readStored(organizationId));

  useEffect(() => {
    setCheckedByOrder(readStored(organizationId));
  }, [organizationId]);

  const persist = useCallback((next) => {
    setCheckedByOrder(next);
    if (organizationId) {
      LocalJsonStorage.set({ key: storageKey(organizationId), value: next });
    }
  }, [organizationId]);

  const isChecked = useCallback((orderId, ingredientId) => (
    Boolean(checkedByOrder[orderId]?.[ingredientId])
  ), [checkedByOrder]);

  const setChecked = useCallback((orderId, ingredientId, checked) => {
    const orderChecks = { ...(checkedByOrder[orderId] || {}) };
    if (checked) {
      orderChecks[ingredientId] = true;
    } else {
      delete orderChecks[ingredientId];
    }

    const next = { ...checkedByOrder };
    if (Object.keys(orderChecks).length === 0) {
      delete next[orderId];
    } else {
      next[orderId] = orderChecks;
    }
    persist(next);
  }, [checkedByOrder, persist]);

  const getProgress = useCallback((orderId, ingredientIds) => {
    const orderChecks = checkedByOrder[orderId] || {};
    const checked = ingredientIds.filter((id) => orderChecks[id]).length;
    return { checked, total: ingredientIds.length };
  }, [checkedByOrder]);

  return {
    isChecked,
    setChecked,
    getProgress,
  };
}
